import { CloseOutlined } from '@ant-design/icons';
import { L } from '@lib';
import { CreatePurchaseOrder, PurchaseOrder, PurchaseOrderStatus, ReceivePurchaseOrder, UpdatePurchaseOrder } from '@services';
import { useStore } from '@stores';
import { Alert, Button, Divider, Drawer, Form, Input, InputNumber, Modal, Space, Tag, Tooltip, Typography } from 'antd';
import { observer } from 'mobx-react';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { SupplierSelect } from '@components';

const { Text } = Typography;

const STATUS_COLORS: Record<string, string> = {
  [PurchaseOrderStatus.Draft]: 'blue',
  [PurchaseOrderStatus.Ordered]: 'orange',
  [PurchaseOrderStatus.Received]: 'green',
  [PurchaseOrderStatus.Canceled]: 'default',
};

export interface PurchaseOrderEditDrawerHandle {
  open: (item: PurchaseOrder) => void;
  openNew: () => void;
}

interface IProps {
  onSaved: () => void;
}

export const PurchaseOrderEditDrawer = observer(
  forwardRef<PurchaseOrderEditDrawerHandle, IProps>(({ onSaved }, ref) => {
    const { purchaseOrderStore } = useStore();
    const [editForm] = Form.useForm();
    const [receiveForm] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState<PurchaseOrder | null>(null);

    useImperativeHandle(ref, () => ({
      open: (item) => {
        setEditing(item);
        editForm.setFieldsValue({
          partName: item.partName,
          partNumber: item.partNumber ?? '',
          reference: item.reference ?? '',
          quantity: item.quantity,
          registrationNumber: item.registrationNumber ?? '',
          vin: item.vin ?? '',
          buildReference: item.buildReference ?? '',
          supplierId: item.supplierId ?? undefined,
        });
        if (item.status === PurchaseOrderStatus.Ordered) {
          receiveForm.setFieldsValue({ receivedQuantity: item.quantity, costPrice: item.costPrice ?? undefined });
        }
        setOpen(true);
      },
      openNew: () => {
        setEditing(null);
        editForm.resetFields();
        receiveForm.resetFields();
        setOpen(true);
      },
    }));

    const handleClose = useCallback(() => {
      setOpen(false);
      editForm.resetFields();
      receiveForm.resetFields();
    }, [editForm, receiveForm]);

    const handleSave = useCallback(async () => {
      const values = await editForm.validateFields();
      if (editing) {
        await purchaseOrderStore.update({ id: editing.id, ...values } as UpdatePurchaseOrder);
      } else {
        await purchaseOrderStore.create(values as CreatePurchaseOrder);
      }
      handleClose();
      onSaved();
    }, [editForm, editing, purchaseOrderStore, handleClose, onSaved]);

    const handleMarkOrdered = useCallback(() => {
      if (!editing) return;
      Modal.confirm({
        title: L('Mark as ordered?'),
        content: `"${editing.partName}" will be placed with ${editing.supplierName ?? 'the supplier'}.`,
        okText: L('Mark as ordered'),
        cancelText: L('Go back'),
        onOk: async () => {
          await purchaseOrderStore.markOrdered(editing.id);
          handleClose();
          onSaved();
        },
      });
    }, [editing, purchaseOrderStore, handleClose, onSaved]);

    const handleReceive = useCallback(async () => {
      if (!editing) return;
      const values = await receiveForm.validateFields();
      await purchaseOrderStore.receive({ id: editing.id, ...values } as ReceivePurchaseOrder);
      handleClose();
      onSaved();
    }, [editing, receiveForm, purchaseOrderStore, handleClose, onSaved]);

    const handleCancel = useCallback(() => {
      if (!editing) return;
      Modal.confirm({
        title: L('Cancel purchase order?'),
        content: `"${editing.partName}" will be canceled.`,
        okText: L('Cancel PO'),
        okType: 'danger',
        cancelText: L('Go back'),
        onOk: async () => {
          await purchaseOrderStore.cancel(editing.id);
          handleClose();
          onSaved();
        },
      });
    }, [editing, purchaseOrderStore, handleClose, onSaved]);

    const status = editing?.status;
    const isDraft = status === PurchaseOrderStatus.Draft;
    const isOrdered = status === PurchaseOrderStatus.Ordered;
    const isLocked = status === PurchaseOrderStatus.Received || status === PurchaseOrderStatus.Canceled;
    // Watch live form value — editing.supplierId is only the value at open time
    const supplierIdWatched = Form.useWatch('supplierId', editForm);
    const hasSupplier = !!(supplierIdWatched ?? editing?.supplierId);

    const footer = (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {(isDraft || isOrdered) && (
            <Button danger onClick={handleCancel} loading={purchaseOrderStore.saving}>
              {L('Cancel PO')}
            </Button>
          )}
        </div>
        <Space>
          {isOrdered && (
            <Button type="primary" loading={purchaseOrderStore.saving} onClick={handleReceive}>
              {L('Receive stock')}
            </Button>
          )}
          {!isLocked && !isOrdered && (
            <Button type="primary" loading={purchaseOrderStore.saving} onClick={handleSave}>
              {L('Save')}
            </Button>
          )}
        </Space>
      </div>
    );

    return (
      <Drawer
        title={
          <Space>
            <span>{editing ? L('Purchase Order') : L('New Purchase Order')}</span>
            {editing && (
              <Tag color={STATUS_COLORS[editing.status] ?? 'default'} style={{ margin: 0 }}>
                {editing.status}
              </Tag>
            )}
          </Space>
        }
        closeIcon={false}
        extra={<CloseOutlined onClick={handleClose} style={{ cursor: 'pointer', fontSize: 16, color: 'rgba(0,0,0,0.45)' }} />}
        open={open}
        onClose={handleClose}
        width={480}
        footer={footer}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="partName" label={L('Part name')} rules={[{ required: true }]}>
            <Input disabled={isLocked || isOrdered} />
          </Form.Item>
          <Form.Item name="quantity" label={L('Quantity')} rules={[{ required: true }]}>
            <InputNumber min={0.01} style={{ width: '100%' }} disabled={isLocked || isOrdered} />
          </Form.Item>
          <Form.Item name="supplierId" label={L('Supplier')}>
            <SupplierSelect disabled={isLocked || isOrdered} />
          </Form.Item>
          <Form.Item name="partNumber" label={L('Part number')}>
            <Input disabled={isLocked || isOrdered} />
          </Form.Item>
          <Form.Item name="reference" label={L('Reference')}>
            <Input disabled={isLocked || isOrdered} />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            {L('Vehicle / job context (optional)')}
          </Text>
          <Form.Item name="registrationNumber" label={L('Registration')}>
            <Input disabled={isLocked || isOrdered} />
          </Form.Item>
          <Form.Item name="vin" label={L('VIN')}>
            <Input disabled={isLocked || isOrdered} />
          </Form.Item>
          <Form.Item name="buildReference" label={L('Build reference')}>
            <Input disabled={isLocked || isOrdered} />
          </Form.Item>
        </Form>

        {/* Mark as ordered — shown inline for Draft, removes it from the footer */}
        {isDraft && (
          <>
            <Divider />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {hasSupplier
                  ? L('Supplier assigned — ready to mark as ordered')
                  : L('Assign a supplier above to mark as ordered')}
              </Text>
              <Tooltip title={hasSupplier ? undefined : L('Assign a supplier first')}>
                <Button
                  type="default"
                  disabled={!hasSupplier}
                  loading={purchaseOrderStore.saving}
                  onClick={handleMarkOrdered}
                >
                  {L('Mark as ordered')}
                </Button>
              </Tooltip>
            </div>
          </>
        )}

        {/* Receive section — shown for Ordered */}
        {isOrdered && (
          <>
            <Divider />
            <Alert
              type="info"
              showIcon
              message={L('Ready to receive')}
              description={L('Enter the received quantity and cost price, then click "Receive stock" to update inventory.')}
              style={{ marginBottom: 16 }}
            />
            <Form form={receiveForm} layout="vertical">
              <Form.Item
                name="receivedQuantity"
                label={L('Received quantity')}
                rules={[{ required: true }]}
              >
                <InputNumber min={0.01} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="costPrice" label={L('Cost price (£)')} rules={[{ required: true }]}>
                <InputNumber min={0} precision={2} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Form>
          </>
        )}
      </Drawer>
    );
  }),
);
